import * as d3 from 'd3';
import type { ConferenceIndex } from '../../data/schema';
import {
  loadHeatmap,
  loadKeywords,
  loadQuoteWall,
  loadTalkNetwork,
  loadThemeFlow,
  loadThemeMap,
  type TalkNetworkPayload,
} from './adapters';
import {
  createConferenceConnections,
  loadCrossTalk,
} from './crossTalk';

export const EXPLORE_VIEWS = [
  'theme-map',
  'theme-flow',
  'heatmap',
  'quote-wall',
  'talk-network',
  'keywords',
  'conference-connections',
] as const;

export type ExploreViewId = (typeof EXPLORE_VIEWS)[number];

export interface ExploreViewDefinition {
  id: ExploreViewId;
  title: string;
  description: string;
  eyebrow: string;
}

function designToken(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

function chartPalette(): string[] {
  const accent = designToken('--color-accent');
  const strong = designToken('--color-accent-chart-strong');
  const mid = designToken('--color-accent-chart-mid');
  const light = designToken('--color-accent-chart-light');
  return [accent, strong, mid, light, accent, strong, mid, light, accent, strong];
}

export const EXPLORE_DEFINITIONS: ExploreViewDefinition[] = [
  {
    id: 'theme-map',
    title: 'Theme Map',
    eyebrow: 'Connections',
    description: 'See talks gathered around the conference themes they share.',
  },
  {
    id: 'theme-flow',
    title: 'Theme Flow',
    eyebrow: 'Across sessions',
    description: 'Follow the strongest themes from talk to talk in session order.',
  },
  {
    id: 'heatmap',
    title: 'Heatmap',
    eyebrow: 'Speaker and theme',
    description: 'Compare theme prominence across every conference speaker.',
  },
  {
    id: 'quote-wall',
    title: 'Quote Wall',
    eyebrow: 'Voices and sources',
    description: 'Browse quoted voices and return to the paragraph using each quote.',
  },
  {
    id: 'talk-network',
    title: 'Talk Network',
    eyebrow: 'Shared references',
    description: 'Trace scripture and source connections between conference talks.',
  },
  {
    id: 'keywords',
    title: 'Keywords',
    eyebrow: 'Language',
    description: 'Find recurring words and the talks where they appear most often.',
  },
  {
    id: 'conference-connections',
    title: 'Conference Connections',
    eyebrow: 'Across the conference',
    description: 'Read the conference narrative, shared themes, and scripture chains together.',
  },
];

function slug(value: string): string {
  return value
    .normalize('NFKD')
    .replaceAll(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, '-')
    .replaceAll(/^-|-$/g, '');
}

function readerHref(talkId: string, paragraphId?: string): string {
  return `/read/${encodeURIComponent(talkId)}${paragraphId ? `/${paragraphId}` : ''}`;
}

function routerLink(href: string, label: string): HTMLAnchorElement {
  const link = document.createElement('a');
  link.href = href;
  link.dataset.router = '';
  link.textContent = label;
  return link;
}

function viewShell(
  definition: ExploreViewDefinition,
): { page: HTMLElement; content: HTMLElement } {
  const page = document.createElement('div');
  page.className = 'explore-view page-width';
  page.dataset.route = 'explore-view';
  page.dataset.exploreView = definition.id;
  const back = routerLink('/explore', 'Back to Explore');
  back.className = 'text-link';
  const header = document.createElement('header');
  header.className = 'explore-heading';
  const eyebrow = document.createElement('p');
  eyebrow.className = 'eyebrow eyebrow--gold';
  eyebrow.textContent = definition.eyebrow;
  const title = document.createElement('h1');
  title.textContent = definition.title;
  const copy = document.createElement('p');
  copy.textContent = definition.description;
  header.append(back, eyebrow, title, copy);
  const content = document.createElement('section');
  content.className = 'explore-canvas card';
  content.setAttribute('aria-label', `${definition.title} visualization`);
  const status = document.createElement('p');
  status.className = 'explore-loading';
  status.setAttribute('role', 'status');
  status.textContent = `Loading ${definition.title}…`;
  content.append(status);
  page.append(header, content);
  return { page, content };
}

function renderError(content: HTMLElement): void {
  const error = document.createElement('div');
  error.className = 'state-panel state-panel--error';
  error.setAttribute('role', 'alert');
  error.textContent =
    'This visualization could not be loaded. Return to Explore and choose another view.';
  content.replaceChildren(error);
}

interface ForceNode extends d3.SimulationNodeDatum {
  id: string;
  label: string;
  href: string;
  group: string;
  radius: number;
}

interface ForceEdge extends d3.SimulationLinkDatum<ForceNode> {
  source: string | ForceNode;
  target: string | ForceNode;
  weight: number;
}

function renderForceGraph(
  target: HTMLElement,
  nodes: ForceNode[],
  edges: ForceEdge[],
  label: string,
): void {
  const width = 920;
  const height = 590;
  const svg = d3
    .select(target)
    .append('svg')
    .attr('class', 'explore-force-graph')
    .attr('viewBox', `0 0 ${String(width)} ${String(height)}`)
    .attr('role', 'img')
    .attr('aria-label', label);
  const links = svg
    .append('g')
    .attr('aria-hidden', 'true')
    .selectAll('line')
    .data(edges)
    .join('line')
    .attr('class', 'explore-graph-link')
    .attr('stroke-width', (edge) => Math.max(1, edge.weight / 2));
  const anchors = svg
    .append('g')
    .selectAll<SVGAElement, ForceNode>('a')
    .data(nodes)
    .join('a')
    .attr('href', (node) => node.href)
    .attr('data-router', '')
    .attr('aria-label', (node) => `Open ${node.label}`)
    .attr('class', (node) => `explore-node explore-node--${node.group}`);
  const groups = [...new Set(nodes.map((node) => node.group))];
  const palette = chartPalette();
  const ink = designToken('--color-ink');
  anchors
    .append('circle')
    .attr('r', (node) => node.radius)
    .style('fill', (node) =>
      node.group === 'theme'
        ? ink
        : palette[Math.max(0, groups.indexOf(node.group)) % palette.length],
    );
  anchors
    .append('text')
    .attr('text-anchor', 'middle')
    .attr('dy', (node) => node.radius + 14)
    .text((node) => node.label.split(/\s+/).slice(-2).join(' '));

  const simulation = d3
    .forceSimulation(nodes)
    .force(
      'link',
      d3
        .forceLink<ForceNode, ForceEdge>(edges)
        .id((node) => node.id)
        .distance((edge) => 150 - Math.min(edge.weight, 10) * 5),
    )
    .force('charge', d3.forceManyBody().strength(-360))
    .force('center', d3.forceCenter(width / 2, height / 2))
    .force(
      'collision',
      d3.forceCollide<ForceNode>().radius((node) => node.radius + 24),
    )
    .stop();
  simulation.tick(220);
  links
    .attr('x1', (edge) => (edge.source as ForceNode).x ?? 0)
    .attr('y1', (edge) => (edge.source as ForceNode).y ?? 0)
    .attr('x2', (edge) => (edge.target as ForceNode).x ?? 0)
    .attr('y2', (edge) => (edge.target as ForceNode).y ?? 0);
  anchors.attr(
    'transform',
    (node) => `translate(${String(node.x ?? 0)},${String(node.y ?? 0)})`,
  );
}

function appendListAlternative(
  target: HTMLElement,
  title: string,
  items: Array<{ href: string; label: string; description?: string }>,
  dense = false,
): void {
  const section = document.createElement('section');
  section.className = `explore-list-alternative${dense ? ' explore-list-alternative--dense' : ''}`;
  const heading = document.createElement('h2');
  heading.textContent = title;
  const copy = document.createElement('p');
  copy.className = 'muted';
  copy.textContent =
    'This list contains the same reading destinations as the visual view.';
  const list = document.createElement('ul');
  for (const item of items) {
    const row = document.createElement('li');
    const link = routerLink(item.href, item.label);
    row.append(link);
    if (item.description) {
      const description = document.createElement('span');
      description.textContent = item.description;
      row.append(description);
    }
    list.append(row);
  }
  section.append(heading, copy, list);
  target.append(section);
}

async function renderThemeMap(
  content: HTMLElement,
  conferenceId: string,
): Promise<void> {
  const payload = await loadThemeMap(conferenceId);
  const strongEdges = payload.edges.filter((edge) => edge.weight >= 7);
  const connected = new Set(
    strongEdges.flatMap((edge) => [edge.source, edge.target]),
  );
  const nodes: ForceNode[] = payload.nodes
    .filter((node) => connected.has(node.id))
    .map((node) => ({
      id: node.id,
      label: node.type === 'talk' ? (node.speaker ?? node.label) : node.label,
      href:
        node.type === 'talk'
          ? readerHref(node.id.replace(/^talk:/, ''))
          : `/threads/theme-${slug(node.label)}`,
      group: node.type,
      radius:
        node.type === 'theme'
          ? 16 + Math.min(18, Math.sqrt(node.size ?? 1))
          : 10,
    }));
  const nodeIds = new Set(nodes.map((node) => node.id));
  const edges: ForceEdge[] = strongEdges
    .filter((edge) => nodeIds.has(edge.source) && nodeIds.has(edge.target))
    .map((edge) => ({ ...edge }));
  content.replaceChildren();
  renderForceGraph(
    content,
    nodes,
    edges,
    'Theme and talk connections. Use the following list for a keyboard equivalent.',
  );
  appendListAlternative(
    content,
    'Theme and talk destinations',
    nodes.map((node) => ({
      href: node.href,
      label: node.label,
      description: node.group === 'theme' ? 'Theme thread' : 'Conference talk',
    })),
  );
}

async function renderThemeFlow(
  content: HTMLElement,
  index: ConferenceIndex,
): Promise<void> {
  const payload = await loadThemeFlow(index.conferenceId);
  const totals = new Map<string, number>();
  for (const talk of payload) {
    for (const theme of talk.themes) {
      totals.set(
        theme.name,
        (totals.get(theme.name) ?? 0) + theme.prominence_score,
      );
    }
  }
  const themes = [...totals.entries()]
    .sort((left, right) => right[1] - left[1])
    .slice(0, 10)
    .map(([name]) => name);
  const rows = index.talks.map((talk) => {
    const source = payload.find((item) => item.talk_id === talk.talkId);
    return {
      talk,
      scores: themes.map(
        (theme) =>
          source?.themes.find((item) => item.name === theme)
            ?.prominence_score ?? 0,
      ),
    };
  });
  content.replaceChildren();
  const width = Math.max(1000, rows.length * 54);
  const height = 510;
  const svg = d3
    .select(content)
    .append('svg')
    .attr('class', 'theme-flow-chart')
    .attr('width', width)
    .attr('height', height)
    .attr('role', 'img')
    .attr(
      'aria-label',
      'Stacked theme prominence by talk. Each segment opens its Reader paragraph.',
    );
  const color = d3.scaleOrdinal(chartPalette()).domain(themes);
  const maxTotal = d3.max(rows, (row) => d3.sum(row.scores)) ?? 1;
  const y = d3.scaleLinear().domain([0, maxTotal]).range([420, 30]);
  const destinations: Array<{
    href: string;
    label: string;
    description: string;
  }> = [];
  for (const [rowIndex, row] of rows.entries()) {
    let cumulative = 0;
    for (const [themeIndex, score] of row.scores.entries()) {
      if (score <= 0) continue;
      const source = payload.find((item) => item.talk_id === row.talk.talkId);
      const theme = themes[themeIndex] ?? '';
      const paragraphId = source?.themes
        .find((item) => item.name === theme)
        ?.evidence_links.find((item) => item.paragraph_id)?.paragraph_id;
      const href = readerHref(row.talk.talkId, paragraphId);
      const segmentHeight = y(cumulative) - y(cumulative + score);
      svg
        .append('a')
        .attr('href', href)
        .attr('data-router', '')
        .attr(
          'aria-label',
          `${row.talk.speaker.name}, ${theme}, prominence ${String(score)} of 10`,
        )
        .append('rect')
        .attr('x', 24 + rowIndex * 54)
        .attr('y', y(cumulative + score))
        .attr('width', 42)
        .attr('height', segmentHeight)
        .attr('fill', color(theme));
      cumulative += score;
      destinations.push({
        href,
        label: `${row.talk.speaker.name}: ${theme}`,
        description: `Prominence ${String(score)} of 10`,
      });
    }
    svg
      .append('text')
      .attr('x', 45 + rowIndex * 54)
      .attr('y', 438)
      .attr('transform', `rotate(55 ${String(45 + rowIndex * 54)} 438)`)
      .attr('font-size', 10)
      .text(row.talk.speaker.name.split(/\s+/).at(-1) ?? '');
  }
  const legend = document.createElement('ul');
  legend.className = 'explore-legend';
  for (const theme of themes) {
    const item = document.createElement('li');
    const swatch = document.createElement('i');
    swatch.style.background = color(theme);
    item.append(swatch, routerLink(`/threads/theme-${slug(theme)}`, theme));
    legend.append(item);
  }
  content.append(legend);
  appendListAlternative(content, 'Theme flow destinations', destinations, true);
}

async function renderHeatmap(
  content: HTMLElement,
  index: ConferenceIndex,
): Promise<void> {
  const payload = await loadHeatmap(index.conferenceId);
  const threshold = 7;
  const themeIndices = payload.themes
    .map((_, themeIndex) => themeIndex)
    .filter((themeIndex) =>
      payload.grid.some((row) => (row[themeIndex] ?? 0) >= threshold),
    )
    .sort(
      (left, right) =>
        d3.sum(payload.grid, (row) => row[right] ?? 0) -
        d3.sum(payload.grid, (row) => row[left] ?? 0),
    );
  const cells = payload.grid.flatMap((row, speakerIndex) =>
    themeIndices.flatMap((themeIndex) => {
      const score = row[themeIndex] ?? 0;
      return score >= threshold
        ? [{ speakerIndex, themeIndex, score }]
        : [];
    }),
  );
  content.replaceChildren();
  const cell = 34;
  const width = 210 + themeIndices.length * cell;
  const height = 175 + payload.speakers.length * cell;
  const svg = d3
    .select(content)
    .append('svg')
    .attr('class', 'heatmap-chart')
    .attr('width', width)
    .attr('height', height)
    .attr('role', 'img')
    .attr(
      'aria-label',
      'Speaker and theme prominence heatmap. Use the following list on a small screen or with a keyboard.',
    );
  const [accent, strong, mid, light] = chartPalette();
  const paper = designToken('--color-paper-raised');
  const ink = designToken('--color-ink');
  const color = d3
    .scaleLinear<string>()
    .domain([7, 8, 9, 10])
    .range([light, mid, strong, accent]);
  for (const [column, themeIndex] of themeIndices.entries()) {
    svg
      .append('text')
      .attr('x', 220 + column * cell)
      .attr('y', 160)
      .attr('transform', `rotate(-55 ${String(220 + column * cell)} 160)`)
      .attr('font-size', 10)
      .text(payload.themes[themeIndex] ?? '');
  }
  for (const [speakerIndex, speaker] of payload.speakers.entries()) {
    svg
      .append('text')
      .attr('x', 195)
      .attr('y', 185 + speakerIndex * cell)
      .attr('text-anchor', 'end')
      .attr('font-size', 11)
      .text(speaker);
  }
  const destinations = cells.map((entry) => {
    const talk = index.talks[entry.speakerIndex];
    const href = readerHref(talk.talkId);
    const theme = payload.themes[entry.themeIndex] ?? '';
    const speaker = payload.speakers[entry.speakerIndex] ?? '';
    const column = themeIndices.indexOf(entry.themeIndex);
    const anchor = svg
      .append('a')
      .attr('href', href)
      .attr('data-router', '')
      .attr(
        'aria-label',
        `${speaker}, ${theme}, prominence ${String(entry.score)} of 10`,
      );
    anchor
      .append('rect')
      .attr('x', 205 + column * cell)
      .attr('y', 166 + entry.speakerIndex * cell)
      .attr('width', cell - 2)
      .attr('height', cell - 2)
      .attr('rx', 3)
      .attr('fill', color(entry.score));
    anchor
      .append('text')
      .attr('x', 221 + column * cell)
      .attr('y', 186 + entry.speakerIndex * cell)
      .attr('text-anchor', 'middle')
      .attr('fill', entry.score >= 8 ? paper : ink)
      .text(String(entry.score));
    return {
      href,
      label: `${speaker}: ${theme}`,
      description: `Prominence ${String(entry.score)} of 10`,
    };
  });
  appendListAlternative(
    content,
    `Prominence ${String(threshold)} and higher`,
    destinations,
    true,
  );
}

function paragraphFromDeepLink(deepLink: string | undefined): string | undefined {
  return deepLink?.match(/(?:id=|#)(p_[A-Za-z0-9]+)/)?.[1];
}

async function renderQuoteWall(
  content: HTMLElement,
  conferenceId: string,
): Promise<void> {
  const payload = await loadQuoteWall(conferenceId);
  const quotes = payload.quotes.slice(0, 120);
  content.replaceChildren();
  const filters = document.createElement('label');
  filters.className = 'explore-filter';
  const filterLabel = document.createElement('span');
  filterLabel.textContent = 'Quote source';
  const select = document.createElement('select');
  select.setAttribute('aria-label', 'Filter quotes by source type');
  const types = ['all', ...new Set(quotes.map((quote) => quote.attribution_type))];
  for (const type of types) {
    const option = document.createElement('option');
    option.value = type;
    option.textContent = type === 'all' ? 'All sources' : type.replaceAll('_', ' ');
    select.append(option);
  }
  filters.append(filterLabel, select);
  const grid = document.createElement('div');
  grid.className = 'quote-wall';
  const draw = (): void => {
    grid.replaceChildren();
    for (const quote of quotes.filter(
      (item) =>
        select.value === 'all' || item.attribution_type === select.value,
    )) {
      const paragraphId = paragraphFromDeepLink(quote.deep_link);
      const card = routerLink(
        readerHref(quote.talk_id, paragraphId),
        quote.full_text ?? quote.text,
      );
      card.className = 'quote-card';
      card.dataset.talkId = quote.talk_id;
      const attribution = document.createElement('span');
      attribution.textContent = `${quote.attribution} · used by ${quote.speaker}`;
      card.append(attribution);
      grid.append(card);
    }
  };
  select.addEventListener('change', draw);
  draw();
  content.append(filters, grid);
}

function networkEdges(payload: TalkNetworkPayload) {
  return payload.edges.top2;
}

async function renderTalkNetwork(
  content: HTMLElement,
  conferenceId: string,
): Promise<void> {
  const payload = await loadTalkNetwork(conferenceId);
  const edges = networkEdges(payload);
  const connected = new Set(edges.flatMap((edge) => [edge.source, edge.target]));
  const nodes: ForceNode[] = payload.nodes
    .filter((node) => connected.has(node.id))
    .map((node) => ({
      id: node.id,
      label: node.speaker,
      href: readerHref(node.id),
      group: node.session,
      radius: 11,
    }));
  const graphEdges: ForceEdge[] = edges.map((edge) => ({ ...edge }));
  content.replaceChildren();
  renderForceGraph(
    content,
    nodes,
    graphEdges,
    'Talk connections by shared scriptures and references. Use the following list for a keyboard equivalent.',
  );
  const nodeById = new Map(payload.nodes.map((node) => [node.id, node]));
  const items = edges.flatMap((edge) => {
    const source = nodeById.get(edge.source);
    const target = nodeById.get(edge.target);
    const shared = edge.shared_items.slice(0, 3).join(', ');
    return [
      {
        href: readerHref(edge.source),
        label: `${source?.speaker ?? edge.source}: ${source?.title ?? ''}`,
        description: `Connected to ${target?.speaker ?? edge.target} by ${shared}`,
      },
      {
        href: readerHref(edge.target),
        label: `${target?.speaker ?? edge.target}: ${target?.title ?? ''}`,
        description: `Connected to ${source?.speaker ?? edge.source} by ${shared}`,
      },
    ];
  });
  appendListAlternative(content, 'Talk connection destinations', items, true);
}

async function renderKeywords(
  content: HTMLElement,
  index: ConferenceIndex,
): Promise<void> {
  const payload = await loadKeywords(index.conferenceId);
  const talks = new Map(index.talks.map((talk) => [talk.talkId, talk]));
  content.replaceChildren();
  const list = document.createElement('div');
  list.className = 'keyword-list';
  for (const keyword of payload.keywords.slice(0, 40)) {
    const row = document.createElement('section');
    row.className = 'keyword-row';
    const heading = document.createElement('h2');
    heading.textContent = keyword.word;
    const total = document.createElement('span');
    total.textContent = `${String(keyword.total_count)} uses`;
    const uses = Object.entries(keyword.per_talk)
      .filter(([, count]) => count > 0)
      .sort((left, right) => right[1] - left[1])
      .slice(0, 6);
    const destinations = document.createElement('div');
    destinations.className = 'keyword-destinations';
    for (const [talkId, count] of uses) {
      const talk = talks.get(talkId);
      if (!talk) continue;
      const link = routerLink(
        readerHref(talkId),
        `${talk.speaker.name.split(/\s+/).at(-1) ?? talk.speaker.name} (${String(count)})`,
      );
      link.setAttribute(
        'aria-label',
        `Open ${talk.title}, which uses ${keyword.word} ${String(count)} times`,
      );
      destinations.append(link);
    }
    row.append(heading, total, destinations);
    list.append(row);
  }
  content.append(list);
}

export function createExploreView(
  view: string,
  index: ConferenceIndex,
): HTMLElement {
  const definition = EXPLORE_DEFINITIONS.find((item) => item.id === view);
  if (!definition) {
    const missing = document.createElement('div');
    missing.className = 'state-panel state-panel--error page-width';
    missing.dataset.route = 'explore-view';
    missing.setAttribute('role', 'alert');
    missing.append(
      document.createTextNode('This Explore view does not exist. '),
      routerLink('/explore', 'Browse the six available views.'),
    );
    return missing;
  }
  if (definition.id === 'conference-connections') {
    const { page, content } = viewShell(definition);
    void loadCrossTalk(index.conferenceId)
      .then((payload) => page.replaceWith(createConferenceConnections(payload)))
      .catch(() => {
        const unavailable = document.createElement('section');
        unavailable.className = 'state-panel';
        const heading = document.createElement('h2');
        heading.textContent = 'Conference Connections is unavailable';
        const copy = document.createElement('p');
        copy.textContent = 'This conference does not include cross-talk analysis.';
        unavailable.append(heading, copy);
        content.replaceChildren(unavailable);
      });
    return page;
  }
  const { page, content } = viewShell(definition);
  const renderer: Promise<void> =
    definition.id === 'theme-map'
      ? renderThemeMap(content, index.conferenceId)
      : definition.id === 'theme-flow'
        ? renderThemeFlow(content, index)
        : definition.id === 'heatmap'
          ? renderHeatmap(content, index)
          : definition.id === 'quote-wall'
            ? renderQuoteWall(content, index.conferenceId)
            : definition.id === 'talk-network'
              ? renderTalkNetwork(content, index.conferenceId)
              : renderKeywords(content, index);
  void renderer.catch(() => renderError(content));
  return page;
}
